<template>
  <div class="full-page-width relative locations" v-if="get_templates.entity.length > 0">
    <ConfirmationBar
      :showSavebar="showSavebar"
      @confirm="saveTemplate"
      @cancel="cancel"
    />
    <AutoMapModal
      :visible="showAutoMapModal"
      :templateType="templateType"
      @close="closeAutoMapModal"
      @apply="applyAutoMapSuggestions"
    />
    <div class="add-button c-float m-b-25">
      <div class="flex-center float-left">
        <Btn
          @click.native="$router.go(-1)"
          class="light bordered empty d-inline-block m-r-15 fix-40"
          :icon="{
            name: 'arrowLeftBig',
            size: '20',
          }" />
        <p class="user-name">{{ `${$t('upload_template')}` }}</p>
      </div>
    </div>
    <div class="row m-b-40" v-if="get_user.is_admin">
      <div class="col-xs-4 text-box"></div>
      <div class="col-xs-8">
        <div class="card row p-40">
          <div class="col-xs-12">
            <InputSelect
              class="m-b-20"
              :multiSelect=true
              :rules="{required: true}"
              :preselected="accessibleForSubaccounts"
              :label="$t('accessible_for_accounts')"
              @change="selectAccessibleSubaccount"
              :options="getAllowedSubaccounts" />
            </div>
        </div>
      </div>
    </div>
    <div class="row m-b-40">
      <div class="col-xs-4 text-box">
        <InfoTextBox
          :text="$t('import-templates-view-info-text-config')"
          :title="$t('add_upload_template')" />
      </div>
      <div class="col-xs-8">
        <div class="card row p-40">
          <div class="col-xs-4">
            <InputText
              class="m-b-40"
              :label="$t('Template name')"
              :rules="{required: true}"
              v-model="templateName" />
          </div>
          <div class="col-xs-4">
            <InputSelect
              class="m-b-40"
              :label="$t('type')"
              v-model="templateType"
              @change="setData"
              :rules="{required: true}"
              :options="getTemplateTypes" />
          </div>
          <div class="col-xs-4">
            <InputSelect
              class="m-b-40"
              :label="$t('file_type')"
              v-model="fileType"
              :rules="{required: true}"
              :options="get_template_field('template_file_type')" />
          </div>
          <div class="col-xs-4">
            <InputSelect
              :label="$t('Reading type')"
              v-model="readingType"
              :rules="{required: true}"
              :options="get_template_field('template_reading_type')" />
          </div>
          <div class="col-xs-4">
            <InputSelect
              :label="$t('Start row')"
              v-model="startRow"
              :hint="'start_row_hint1'"
              :rules="{required: true}"
              :options="rowData" />
          </div>
          <div class="col-xs-4">
            <InputSelect
              :label="$t('data_separator')"
              v-model="separator"
              :rules="{required: true}"
              :options="get_template_field('template_separator')" />
          </div>
        </div>
      </div>
    </div>
    <hr class="m-b-40"/>
    <div class="row" v-if="templateType !== ''">
      <div class="col-xs-4 text-box">
        <InfoTextBox
          :text="$t('import-templates-view-info-text-map')"
          :title="$t('Select template fields')" />
        <Btn
          @click.native="openAutoMapModal"
          class="brand float-left m-t-20"
          :icon="{
            name: 'sparkle',
            size: '18',
          }"
          :btnText="$t('auto_map')"/>
      </div>
      <div class="col-xs-8">
        <div class="card row p-40">
          <!-- Colli/Content count inputs for shipment fields -->
          <div v-if="isShipmentFields" class="col-xs-12 m-b-30">
            <div class="row">
              <div class="col-xs-3">
                <InputSelect
                  :label="$t('Number of colli')"
                  v-model="colliCount"
                  @change="onColliContentChange"
                  :options="colliOptions" />
              </div>
              <div class="col-xs-3">
                <InputSelect
                  :label="$t('Content per colli')"
                  v-model="contentCount"
                  @change="onColliContentChange"
                  :options="contentOptions" />
              </div>
              <div class="col-xs-6" v-if="isLoadingFields">
                <p class="loading-fields-text">{{ $t('Loading fields...') }}</p>
              </div>
            </div>
          </div>
          <!-- All fields in virtual scroller -->
          <div class="col-xs-12">
            <div class="row m-b-10">
              <div class="col-xs-5">
                <h2 class="c-title">{{ $t('header_text') }}</h2>
                <small class="c-light">{{ $t('Type in header text from your file') }}</small>
              </div>
              <div class="col-xs-6">
                <h2 class="c-title">{{ $t('Mapping field') }}</h2>
                <small class="c-light">{{ $t('Select field you will map your data to') }}</small>
              </div>
            </div>

            <RecycleScroller
              class="fields-scroller"
              :items="allFieldsCombined"
              :item-size="65"
              key-field="id"
              v-slot="{ item }">
              <div class="row scroller-row">
                <!-- Required field -->
                <template v-if="item.type === 'required'">
                  <div class="col-xs-5">
                    <InputText
                      v-model="requiredFields[item.field_name]"
                      placeholder="Type in header text from your file" />
                  </div>
                  <div class="col-xs-6">
                    <InputText
                      :value="item.field_name"
                      disabled />
                  </div>
                  <div class="col-xs-1">
                  </div>
                </template>
                <!-- Additional field -->
                <template v-else>
                  <div class="col-xs-5">
                    <InputText
                      v-model="item.file_column"
                      placeholder="Type in header text from your file" />
                  </div>
                  <div class="col-xs-6">
                    <MappingSelect
                      v-model="item.field_name"
                      :options="additionalFieldsOptions"
                      @change="selectField(item.index, $event)" />
                  </div>
                  <div class="col-xs-1 text-center">
                    <Btn
                      @click.native="removeField(item.index)"
                      class="gray btn-delete-field"
                      position="icon-pos-center"
                      :icon="{ name: 'deleteForever', size: '16' }" />
                  </div>
                </template>
              </div>
            </RecycleScroller>
          </div>
          <div class="col-xs-12">
            <Btn
              v-if="!isAddDisabled"
              @click.native="addField"
              class="brand float-right"
              :icon="{
                name: 'plus',
                size: '18',
              }"
              :btnText="$t('add_field')"/>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
/* eslint-disable */
import { mapGetters } from 'vuex';
import Btn from '@/components/elements/buttons';
import InputText from '@/components/elements/inputs/text.vue';
import InputSelect from '@/components/elements/inputs/select.vue';
import MappingSelect from '@/components/elements/inputs/MappingSelect.vue';
import ConfirmationBar from '@/components/global/confirmationBar';
import InfoTextBox from '@/components/global/infoTextBox';
import AutoMapModal from '@/components/global/AutoMapModal';
import EventBus from '@/eventBus.js';
import formValidator from '@/mixins/formValidator';
import { RecycleScroller } from 'vue-virtual-scroller';
import 'vue-virtual-scroller/dist/vue-virtual-scroller.css';

export default {
  name: 'addTemplate',
  mixins: [formValidator],
  components: { Btn, InputText, InputSelect, MappingSelect, InfoTextBox, ConfirmationBar, AutoMapModal, RecycleScroller },
  data() {
    return {
      accessibleForSubaccounts: [],
      templateName: '',
      templateType: '',
      fileType: '',
      readingType: '',
      startRow: '',
      separator: '',
      requiredFields: {},
      additionalFieldsData: [],
      additionalFieldsOptions: [],
      // Confirmation savebar
      showSavebar: false,
      savebarEnabled: false,
      // Auto Map Modal
      showAutoMapModal: false,
      // Colli/content counts for shipment_fields
      colliCount: 1,
      contentCount: 1,
      dgCount: 1,
      isLoadingFields: false,
    };
  },
  computed: {
    ...mapGetters([
      'get_templates',
      'get_template_field',
      'get_subaccounts',
      'get_user'
    ]),
    getAllowedSubaccounts() {
      return this.get_subaccounts.filter((acc) => this.get_user.accessible_subaccounts_array.indexOf(String(acc.id)) > -1).map((acc) => ({text: acc.name, value: String(acc.id),}));
    },
    rowData() {
      const objArray = [];
      for (let ii = 1; ii < 20; ii++) {
        objArray.push({
          text: ii.toString(),
          value: ii,
        });
      }
      return objArray;
    },
    getTemplateTypes() {
      let result = [];
      for(const property in this.get_templates.entity[0]){
        if(['item_fields','customer_fields', 'shipment_fields', 'user_fields'].includes(property)){
          result.push({
            text: property,
            value: property,
          });
        }
      };
      return result;
    },
    isAddDisabled() {
      return this.additionalFieldsOptions.length == this.additionalFieldsData.length;
    },
    colliOptions() {
      const options = [];
      for (let i = 1; i <= 10; i++) {
        options.push({ text: i.toString(), value: i });
      }
      return options;
    },
    contentOptions() {
      const options = [];
      for (let i = 1; i <= 25; i++) {
        options.push({ text: i.toString(), value: i });
      }
      return options;
    },
    isShipmentFields() {
      return this.templateType === 'shipment_fields';
    },
    allFieldsCombined() {
      const combined = [];
      // Add required fields first
      if (this.templateType && this.get_templates.entity[0] && this.get_templates.entity[0][this.templateType]) {
        this.get_templates.entity[0][this.templateType].forEach((field, idx) => {
          if (field.required) {
            combined.push({
              id: `req-${idx}`,
              type: 'required',
              field_name: field.field,
            });
          }
        });
      }
      // Add additional fields - pass original object reference
      this.additionalFieldsData.forEach((item, idx) => {
        item.id = `add-${idx}`;
        item.type = 'additional';
        combined.push(item);
      });
      return combined;
    },
  },
  created() {
    this.$store.dispatch('GET_TEMPLATES');
    this.$nextTick(() => {
      this.savebarEnabled = true;
    })
  },
  methods: {
    selectAccessibleSubaccount(val) {
      if(val.length > 0){
        this.accessibleForSubaccounts = val;
      }
    },
    cancel() {
      this.showSavebar = false;
    },
    saveTemplate() {
      EventBus.$emit('checkSubmitErrors');
      if(this.get_user.is_admin){
        if(this.accessibleForSubaccounts.length == 0) return;
        this.accessibleForSubaccounts = this.accessibleForSubaccounts.filter((val) => this.get_user.accessible_subaccounts_array.indexOf(String(val)) > -1);
      }
      // CHEK FORM
      let formWithValidators = [
        {
          formFeild:'templateName',
          value: this.templateName,
          validators:{required: true}
        },
        {
          formFeild:'templateType',
          value: this.templateType,
          validators:{required: true}
        },
        {
          formFeild:'fileType',
          value: this.fileType,
          validators:{required: true}
        },
        {
          formFeild:'readingType',
          value: this.readingType,
          validators:{required: true}
        },
        {
          formFeild:'startRow',
          value: this.startRow,
          validators:{required: true}
        },
        {
          formFeild:'separator',
          value: this.separator,
          validators:{required: true}
        },
      ]

      if(!this.validateInput(formWithValidators)){
        this.$store.dispatch('CREATE_TEMPLATE', {
          accessible_for_subaccounts: this.accessibleForSubaccounts,
          name: this.templateName,
          template_type: this.templateType,
          template_reading_type: this.readingType,
          template_separator: this.separator,
          template_file_type: this.fileType,
          sheet_number: 1,
          start_from_row: this.startRow,
          fields_json: this.spreadFields(),
        }).then((res) => {
          // console.log(res.data.items.id)
          this.showSavebar = false;
          let url = res.data.items.id ? `/settings/edit-template/${res.data.items.id}` : '/settings/platform/import-templates'
          this.$router.push(url)
        }).catch((e) => {
          this.showSavebar = false;
        })
      }
    },
    removeField(index) {
      const fieldName = this.additionalFieldsData[index].field_name;
      for(const prop in this.additionalFieldsOptions){
        if(this.additionalFieldsOptions[prop].value == fieldName){
          this.additionalFieldsOptions[prop].selected = false;
        }
      }

      this.additionalFieldsData.splice(index, 1);
      // Re-index for RecycleScroller key-field
      this.additionalFieldsData.forEach((f, i) => { f.index = i; });
    },
    spreadFields() {
      const fields = [];
      for(const item in this.requiredFields) {
        fields.push({
          field_name: item,
          file_column: this.requiredFields[item],
        });
      }

      this.additionalFieldsData.forEach((obj, i) => {
        fields.push({
          field_name: obj.field_name,
          file_column: obj.file_column,
        });
      });

      return fields;
    },
    addField() {
      this.additionalFieldsData.push({
        index: this.additionalFieldsData.length,
        file_column: '',
        field_name: ''
      });
      this.$nextTick(() => {
        const scroller = this.$el.querySelector('.fields-scroller');
        if (scroller) scroller.scrollTop = scroller.scrollHeight;
      });
    },
    selectField(index, val) {
      //Give a little time to v-model to do the stuff
      setTimeout(() => {
        //Reset additional fields options list
        for(const prop in this.additionalFieldsOptions){
          if(this.additionalFieldsOptions[prop].value != val){
            this.additionalFieldsOptions[prop].selected = false;
          }

          //Mark currently selected field
          if (this.additionalFieldsOptions[prop].value === val){
            this.additionalFieldsOptions[prop].selected = true;
          }

          //Mark already added fields as selected
          for (let i = 0; i < this.additionalFieldsData.length; i++) {
            if (this.additionalFieldsData[i].field_name === this.additionalFieldsOptions[prop].value) {
              this.additionalFieldsOptions[prop].selected = true;
            }
          }
        }
      }, 50)
    },
    setData(val) {
      this.requiredFields = new Object();
      this.additionalFieldsOptions = new Array();
      this.additionalFieldsData = [];
      // Reset colli/content counts when changing template type
      this.colliCount = 1;
      this.contentCount = 1;
      this.dgCount = 1;
      const obj = this.get_templates.entity[0][val];
      for(let ii=0; ii < obj.length; ii++){
        if( obj[ii].required ){
          this.$set(this.requiredFields, [obj[ii].field], '');
        } else {
          this.additionalFieldsOptions.push({
            text: obj[ii].field,
            value: obj[ii].field,
            selected: false,
          });
        }

      }
    },
    async onColliContentChange() {
      if (!this.isShipmentFields) return;

      this.isLoadingFields = true;
      try {
        const response = await this.$store.dispatch('GET_DYNAMIC_TEMPLATE_FIELDS', {
          type: this.templateType,
          colli: this.colliCount,
          content: this.contentCount,
          dg: this.dgCount,
        });

        if (response && response.fields) {
          // Preserve currently selected fields
          const currentSelections = this.additionalFieldsData.map(f => ({
            field_name: f.field_name,
            file_column: f.file_column,
          }));

          // Rebuild additionalFieldsOptions with new fields
          this.additionalFieldsOptions = [];
          const newRequiredFields = {};

          for (const field of response.fields) {
            if (field.required) {
              // Preserve existing value if available
              const existingValue = this.requiredFields[field.field] || '';
              newRequiredFields[field.field] = existingValue;
            } else {
              const isSelected = currentSelections.some(s => s.field_name === field.field);
              this.additionalFieldsOptions.push({
                text: field.field,
                value: field.field,
                selected: isSelected,
              });
            }
          }

          this.requiredFields = newRequiredFields;

          // Filter additionalFieldsData to only keep valid fields
          this.additionalFieldsData = currentSelections.filter(s =>
            this.additionalFieldsOptions.some(o => o.value === s.field_name)
          ).map((s, idx) => ({
            index: idx,
            field_name: s.field_name,
            file_column: s.file_column,
          }));
        }
      } catch (err) {
        console.error('Failed to load dynamic fields:', err);
      } finally {
        this.isLoadingFields = false;
      }
    },
    openAutoMapModal() {
      this.showAutoMapModal = true;
    },
    closeAutoMapModal() {
      this.showAutoMapModal = false;
    },
    applyAutoMapSuggestions(suggestions) {
      if (!suggestions || !Array.isArray(suggestions)) return;

      // Batch updates to avoid triggering watchers multiple times
      const newRequiredFields = { ...this.requiredFields };
      const newAdditionalFieldsData = [...this.additionalFieldsData];
      const optionsToMark = [];

      suggestions.forEach(suggestion => {
        const fieldName = suggestion.field_name;
        const fileColumn = suggestion.file_column;

        // Check if it's a required field
        if (newRequiredFields.hasOwnProperty(fieldName)) {
          newRequiredFields[fieldName] = fileColumn;
        } else {
          // Check if field is already in additionalFieldsData
          const existingIndex = newAdditionalFieldsData.findIndex(f => f.field_name === fieldName);
          if (existingIndex === -1) {
            // Check if field exists in options
            const optionExists = this.additionalFieldsOptions.some(o => o.value === fieldName);
            if (optionExists) {
              // Add new field
              newAdditionalFieldsData.push({
                index: newAdditionalFieldsData.length,
                file_column: fileColumn,
                field_name: fieldName
              });
              optionsToMark.push(fieldName);
            }
          } else {
            // Update existing field
            newAdditionalFieldsData[existingIndex].file_column = fileColumn;
          }
        }
      });

      // Apply batched updates in single assignments
      this.requiredFields = newRequiredFields;
      this.additionalFieldsData = newAdditionalFieldsData;

      // Mark options as selected
      optionsToMark.forEach(fieldName => {
        const optionIndex = this.additionalFieldsOptions.findIndex(o => o.value === fieldName);
        if (optionIndex !== -1) {
          this.additionalFieldsOptions[optionIndex].selected = true;
        }
      });

      this.showSavebar = true;
    },
  },watch: {
    'accessibleForSubaccounts': {
      handler(){
        if(this.savebarEnabled){
          this.showSavebar = true;
        }
      },
      deep: true
    },
    'templateName': {
      handler(){
        if(this.savebarEnabled){
          this.showSavebar = true;
        }
      },
      deep: true
    },
    'templateType': {
      handler(){
        if(this.savebarEnabled){
          this.showSavebar = true;
        }
      },
      deep: true
    },
    'fileType': {
      handler(){
        if(this.savebarEnabled){
          this.showSavebar = true;
        }
      },
      deep: true
    },
    'startRow': {
      handler(){
        if(this.savebarEnabled){
          this.showSavebar = true;
        }
      },
      deep: true
    },
    'separator': {
      handler(){
        if(this.savebarEnabled){
          this.showSavebar = true;
        }
      },
      deep: true
    },
    'requiredFields': {
      handler(){
        if(this.savebarEnabled){
          this.showSavebar = true;
        }
      },
      deep: true
    },
    'additionalFieldsData': {
      handler(){
        if(this.savebarEnabled){
          this.showSavebar = true;
        }
      },
      deep: true
    },
  },

}
</script>
<style scoped>
.loading-fields-text {
  line-height: 45px;
  color: #666;
  font-style: italic;
}

.fields-scroller {
  height: 600px;
  overflow-y: auto;
  overflow-x: visible;
  border: 1px solid #DBDFE3;
  border-radius: 4px;
  padding: 8px;
}

.fields-scroller >>> .vue-recycle-scroller__item-wrapper {
  overflow: visible;
}

.fields-scroller >>> .vue-recycle-scroller__item-view {
  overflow: visible;
}

.scroller-row {
  height: 65px;
  display: flex;
  align-items: center;
  padding-bottom: 10px;
  box-sizing: border-box;
}

.btn-delete-field {
  width: 36px;
  height: 36px;
  min-width: 36px;
  padding: 0;
}
</style>
